import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vehiclesApi } from '../api/vehicles';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';
import { LazyVehicleForm } from '../components/Forms/LazyForms';

const Vehicles = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicles', page, pageSize],
    queryFn: () => vehiclesApi.getAll({ page, pageSize }),
  });

  const deleteMutation = useMutation({
    mutationFn: vehiclesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Vehículo eliminado exitosamente');
    },
    onError: (error) => {
      // Manejar errores específicos del servidor
      if (error.response?.status === 409) {
        // Error de conflicto - vehículo tiene viajes asignados
        const message = error.response?.data?.message || 'No se puede eliminar el vehículo porque tiene viajes asignados';
        toast.error(message);
      } else if (error.response?.status) {
        // Otros errores HTTP
        const message = error.response?.data?.message || `Error ${error.response.status}: ${error.response.statusText}`;
        toast.error(message);
      } else {
        // Errores de red u otros
        toast.error(error.message || 'Error al eliminar vehículo');
      }
    },
  });

  // Handle both array and wrapped response formats
  const vehicles = Array.isArray(data) ? data : (data?.data || []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'right', marginBottom: '20px' }}>
        {hasPermission('canManageVehicles') && (
          <button 
            className="btn btn--primary" 
            onClick={() => {
              setEditingVehicle(null);
              setShowForm(true);
            }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>add</span>
            Nuevo Vehículo
          </button>
        )}
      </div>

      <div style={{ marginTop: 0 }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7, color: '#ef4444' }}>
            Error al cargar vehículos: {error.message || 'Error desconocido'}
          </div>
        ) : vehicles.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>No hay vehículos disponibles</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid rgba(255,255,255,.10)', background: 'var(--panel-bg)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,.07)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Marca</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Modelo</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Año</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Placas</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Capacidad</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Estado</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle) => (
                  <tr key={vehicle.id} style={{ borderTop: '1px solid rgba(255,255,255,.08)', color: '#e5e5e5' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '600' }}>{vehicle.marca}</td>
                    <td style={{ padding: '12px 16px' }}>{vehicle.modelo}</td>
                    <td style={{ padding: '12px 16px' }}>{vehicle.ano}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace' }}>{vehicle.placas}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {vehicle.capacidad_pasajeros} pasajeros<br/>
                      <small style={{ opacity: 0.7 }}>
                        {vehicle.equipaje === 1 || vehicle.equipaje === true ? 'Con equipaje' : 'Sin equipaje'}
                        {vehicle.tipo_vehiculo && ` • ${vehicle.tipo_vehiculo}`}
                        {vehicle.economico && ` • Económico: ${vehicle.economico}`}
                      </small>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span className={`badge ${vehicle.activo === 1 || vehicle.activo === true ? 'green' : 'gray'}`}>
                        {vehicle.activo === 1 || vehicle.activo === true ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {hasPermission('canManageVehicles') ? (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                          {/* Editar */}
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => {
                                setEditingVehicle(vehicle);
                                setShowForm(true);
                              }}
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#3b82f6',
                                color: '#fff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#2563eb';
                                e.currentTarget.style.transform = 'scale(1.05)';
                                const tooltip = e.currentTarget.nextElementSibling;
                                if (tooltip) tooltip.style.opacity = '1';
                                if (tooltip) tooltip.style.visibility = 'visible';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#3b82f6';
                                e.currentTarget.style.transform = 'scale(1)';
                                const tooltip = e.currentTarget.nextElementSibling;
                                if (tooltip) tooltip.style.opacity = '0';
                                if (tooltip) tooltip.style.visibility = 'hidden';
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>edit</span>
                            </button>
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                padding: '6px 12px',
                                background: '#1a1a1a',
                                color: '#fff',
                                fontSize: '12px',
                                borderRadius: '6px',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                opacity: 0,
                                visibility: 'hidden',
                                transition: 'opacity 0.2s, visibility 0.2s',
                                zIndex: 1000,
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              Editar
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid #1a1a1a'
                                }}
                              />
                            </div>
                          </div>

                          {/* Eliminar */}
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => {
                                if (confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
                                  deleteMutation.mutate(vehicle.id);
                                }
                              }}
                              disabled={deleteMutation.isPending}
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#ef4444',
                                color: '#fff',
                                cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                opacity: deleteMutation.isPending ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!deleteMutation.isPending) {
                                  e.currentTarget.style.background = '#dc2626';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '1';
                                  if (tooltip) tooltip.style.visibility = 'visible';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#ef4444';
                                e.currentTarget.style.transform = 'scale(1)';
                                const tooltip = e.currentTarget.nextElementSibling;
                                if (tooltip) tooltip.style.opacity = '0';
                                if (tooltip) tooltip.style.visibility = 'hidden';
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>delete</span>
                            </button>
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                padding: '6px 12px',
                                background: '#1a1a1a',
                                color: '#fff',
                                fontSize: '12px',
                                borderRadius: '6px',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                opacity: 0,
                                visibility: 'hidden',
                                transition: 'opacity 0.2s, visibility 0.2s',
                                zIndex: 1000,
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              Eliminar
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid #1a1a1a'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span style={{ opacity: 0.5, fontSize: '14px' }}>Sin permisos</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <LazyVehicleForm
          vehicle={editingVehicle}
          onClose={() => {
            setShowForm(false);
            setEditingVehicle(null);
          }}
          onSuccess={() => {
            setShowForm(false);
            setEditingVehicle(null);
          }}
        />
      )}
    </div>
  );
};

export default Vehicles;
